import { useEffect, useState } from 'react'
import type { ConnexionGoogle } from './connexion'
import { injecterStylesGoogle } from './styles'

/** Le « G » officiel de Google : quatre couleurs, jamais recolorié. */
export function LogoG() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  )
}

/** Séparateur « ou » + bouton (rendu pur, testable). */
export function AspectBoutonGoogle({ attente, desactive, surClic }: { attente: boolean; desactive?: boolean; surClic: () => void }) {
  injecterStylesGoogle()
  return (
    <>
      <div className="gg-ou" role="separator" aria-label="ou"><span>ou</span></div>
      <button type="button" className="gg-bouton" onClick={surClic} disabled={attente || desactive === true} aria-busy={attente || undefined}>
        <LogoG />
        <span>{attente ? 'Redirection…' : 'Continuer avec Google'}</span>
      </button>
    </>
  )
}

/**
 * « ou » + « Continuer avec Google » sous le formulaire e-mail / mot de passe.
 *
 * Le bouton est MASQUÉ tant que le serveur n'a pas confirmé que Google est activé pour ce produit (`/auth/v1/settings`) :
 * fournisseur absent, produit non branché, réseau coupé → rien ne s'affiche, la connexion par e-mail reste seule.
 * `surErreur('')` efface le message précédent ; les erreurs remontent au formulaire (au même endroit que celles du mot de passe).
 */
export function BoutonGoogle({
  connexion,
  surErreur,
  desactive,
}: {
  connexion: ConnexionGoogle
  surErreur: (message: string) => void
  desactive?: boolean
}) {
  const [actif, setActif] = useState(false)
  const [attente, setAttente] = useState(false)

  useEffect(() => {
    let vivant = true
    void connexion.fournisseurActif().then((r) => vivant && setActif(r === true))
    return () => {
      vivant = false
    }
  }, [connexion])

  /* Retour arrière depuis Google : la page revient du cache avec le bouton « occupé ». */
  useEffect(() => {
    const revenir = (e: PageTransitionEvent) => {
      if (e.persisted) setAttente(false)
    }
    window.addEventListener('pageshow', revenir)
    return () => window.removeEventListener('pageshow', revenir)
  }, [])

  if (!actif) return null

  async function cliquer() {
    setAttente(true)
    surErreur('')
    try {
      await connexion.continuer()
      /* Succès : le navigateur part chez Google. Le bouton reste occupé. */
    } catch (e) {
      surErreur(e instanceof Error ? e.message : 'Connexion Google impossible.')
      setAttente(false)
    }
  }

  return <AspectBoutonGoogle attente={attente} {...(desactive !== undefined ? { desactive } : {})} surClic={() => void cliquer()} />
}
